import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../redux/slices/appContext'
import { integrations, postNamedCommand, postNamedQuery } from '../service'
import { REDTAIL_INTEGRATION } from '../constants'
import useDebouncedValue from '../hooks/useDebounceValue'
import { QUERY_KEYS } from './queryKeys'

const REDTAIL_CONTACT_INDIVIDUAL_TYPE = 'Individual'

export const useRedtailSyncStatus = (clientId, queryOptions) => {
  const { userId } = useAppContext()
  const { mapper, refetchInterval, enabled = true } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.redtailSyncStatus, userId, clientId],
    queryFn: async () => {
      const { data } = await integrations[REDTAIL_INTEGRATION].syncStatus(
        clientId
      )
      return data
    },
    refetchInterval,
    select: mapper,
    enabled
  })
}

export const useRedtailDomainQuery = (queryName, request, options = {}) => {
  const { userId, clientId } = useAppContext()
  const { enabled = true, ...restOptions } = options
  return useQuery({
    queryKey: [QUERY_KEYS.redtailDomainQuery, userId, clientId, queryName, request],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-query', {
        query: queryName,
        autoResolveConnection: true,
        clientId,
        request
      })
      return data
    },
    enabled: enabled && !!clientId,
    ...restOptions
  })
}

const BATCH_SIZE = 10

export const postBatchRedtailQueries = async (queries, { clientId, autoResolveConnection = true } = {}) => {
  const chunks = []
  for (let i = 0; i < queries.length; i += BATCH_SIZE) {
    chunks.push(queries.slice(i, i + BATCH_SIZE))
  }

  const allResults = []
  for (const chunk of chunks) {
    const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-queries', {
      queries: chunk,
      clientId,
      autoResolveConnection
    })
    allResults.push(...(data?.results ?? []))
  }

  return { results: allResults }
}

export const useRedtailClientLink = (clientId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.redtailFamilyContext, 'link', userId, clientId],
    queryFn: async () => {
      try {
        const { data } = await postNamedQuery('coreData', 'resolve-redtail-identifier', {
          type: 'client',
          id: clientId
        })
        return { linked: true, externalId: data.externalId }
      } catch (err) {
        if (err?.response?.status === 400) {
          return { linked: false, externalId: null }
        }
        throw err
      }
    },
    enabled: !!clientId
  })
}

export const useRedtailFamilyContext = (clientIdOverride) => {
  const { userId, clientId: contextClientId } = useAppContext()
  const clientId = clientIdOverride ?? contextClientId

  const link = useRedtailClientLink(clientId)
  const familyId = link.data?.externalId ?? null

  const membersQuery = useQuery({
    queryKey: [QUERY_KEYS.redtailFamilyContext, 'members', userId, clientId, familyId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-query', {
        query: 'listFamilyMembers',
        autoResolveConnection: true,
        clientId,
        request: { familyId }
      })
      return data?.family_members ?? data?.members ?? []
    },
    enabled: !!familyId
  })

  return {
    isLoading: link.isLoading || (!!familyId && membersQuery.isFetching),
    linked: link.data?.linked ?? null,
    familyId,
    members: membersQuery.data ?? [],
    error: link.error || membersQuery.error
  }
}

export const useRedtailContactSearch = (query, { enabled = true, clientId } = {}) => {
  const { userId } = useAppContext()
  const trimmed = (query ?? '').trim()
  const debouncedTerm = useDebouncedValue(trimmed, 350)
  // Mode is keyed so the recently-viewed and search caches don't collide.
  const isSearching = debouncedTerm.length >= 2
  const mode = isSearching ? 'search' : 'recent'
  return useQuery({
    queryKey: [QUERY_KEYS.redtailContactSearch, userId, clientId, mode, isSearching ? debouncedTerm : null],
    queryFn: async () => {
      const queryName = isSearching ? 'searchContactsBasic' : 'listRecentlyViewedContacts'
      const request = isSearching
        ? { params: { name: debouncedTerm, type: REDTAIL_CONTACT_INDIVIDUAL_TYPE } }
        : {}
      const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-query', {
        query: queryName,
        autoResolveConnection: true,
        clientId,
        request
      })
      // Only Individuals can be HOH; tolerate missing `type` since search_basic can elide it.
      return (data?.contacts ?? data?.results ?? [])
        .filter(r => r?.type === REDTAIL_CONTACT_INDIVIDUAL_TYPE || !r?.type)
        .slice(0, 10)
    },
    enabled: enabled && !!clientId
  })
}

export const useLinkRedtailClient = () => {
  const queryClient = useQueryClient()
  const { userId, clientId: contextClientId } = useAppContext()

  return useMutation({
    mutationFn: async ({ clientId, primaryContactId, familyName, acknowledgeConflict }) => {
      const targetClientId = clientId ?? contextClientId
      const { data } = await postNamedCommand('coreData', 'link-redtail-client', {
        clientId: targetClientId,
        primaryContactId,
        ...(familyName ? { familyName } : {}),
        ...(acknowledgeConflict ? { acknowledgeConflict: true } : {})
      })
      return data
    },
    onSuccess: (data, variables) => {
      const targetClientId = variables.clientId ?? contextClientId
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.redtailFamilyContext, 'link', userId, targetClientId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.redtailFamilyContext, 'members', userId, targetClientId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.redtailSyncStatus, userId, targetClientId] })
      // Predicate (not prefix) because consumers vary positionally (preset hooks
      // insert a tag before userId).
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === QUERY_KEYS.redtailDomainQuery &&
          queryKey.includes(userId) &&
          queryKey.includes(targetClientId)
      })
    }
  })
}
